import { useEffect, useState } from 'react';
import Helmet from 'react-helmet';

import { IonSpinner } from '@ionic/react';
import { getFunctions, httpsCallable } from 'firebase/functions';

import styles from './InviteTeammates.module.scss';

import {
  defaultError,
  invalidParametersError,
  inviteError
} from '../../config/errors.config';
import { APP_TITLE } from '../../constants/app';
import {
  sessionEmailsSentKey,
  sessionValidationKey
} from '../../constants/storage';
import { app } from '../../firebase';
import { getService } from '../../services';
import { ServiceError } from '../../services/errors';
import { Service } from '../../services/service';
import { usePricingPlansStore } from '../../stores/usePricingPlansStore';
import { ErrorCard } from '../../stories/ErrorCard/ErrorCard';
import InviteTeammatesCard from '../../stories/InviteTeammatesCard/InviteTeammatesCard';
import { InviteTeamMemberParams } from '../../types/email.types';
import { ValidationResponse } from '../../types/model.types';
import {
  Organization,
  Owner,
  Repository,
  Teammate,
  User
} from '../../types/services.types';
import { sanitize } from '../../util/sanitize';
import { get, set } from '../../util/sessionStorage';
import { pricingPlanSchema } from '../../validations/pricingPlans';

export default function InviteTeammates() {
  const { params } = usePricingPlansStore();

  const [loading, setLoading] = useState(true);
  const [inviteToken, setInviteToken] = useState<string>();
  const [service, setService] = useState<Service>();
  const [user, setUser] = useState<User>();
  const [token, setToken] = useState<string>();
  const [organization, setOrganization] = useState<Organization>();
  const [repository, setRepository] = useState<Repository>();
  const [teammates, setTeammates] = useState<Teammate[]>([]);
  const [error, setError] = useState<ServiceError>();

  useEffect(() => {
    setup();
  }, []);

  const setup = async () => {
    const paramsParsed = pricingPlanSchema.safeParse(params);

    if (!paramsParsed.success) {
      setError(
        new ServiceError(
          invalidParametersError.title,
          invalidParametersError.message
        )
      );

      return;
    }

    const {
      service,
      host,
      pat,
      username,
      token,
      org,
      repo,
      scheme,
      ide,
      machine
    } = paramsParsed.data;

    const codeService = getService(service, host, Boolean(pat), username);

    setService(codeService);
    setToken(token);

    try {
      // Run request in parallel
      const [user, currentUserEmails, organization] = await Promise.all([
        // Get the current user
        codeService.getUser(token, org),
        // Check if we have the email permissions
        codeService.getUserEmails(token, org),
        // Check if we have access to orgs
        codeService.getOrganization(token, org)
      ]);

      // If they signed in with a repo (repo param present) then only get users from that repo
      // Else they signed in with an org and we can get users from the the 5 most recent repos
      const reposForTeammates = await codeService.getMostRecentRepos(
        token,
        org
      );

      // Get the last 100 commits from the last 5 active repos
      const commits = await codeService.getLatestCommitsFromRepos(
        token,
        org,
        reposForTeammates
      );

      // Get the invite token and members here, skip if we have come from the confirmation email
      const owner: Owner = {
        id: organization.id,
        name: organization.displayName,
        type: organization.type,
        createdAt: organization.createdAt,
        enterprise: organization.enterprise
      };

      // TODO: There might be an issue with users from self hosted trying to invite
      const { inviteToken, members } = await getInviteTokenAndMembers(
        token,
        service,
        host,
        username,
        user.displayName,
        user.email || currentUserEmails[0],
        user.id,
        owner,
        scheme,
        ide,
        machine
      );

      setInviteToken(inviteToken);
      const orgMembers = members || [];

      // Get emails sent already
      const sentEmailsString = get(sessionEmailsSentKey);
      const sentEmails: string[] = sentEmailsString
        ? JSON.parse(sentEmailsString)
        : [];

      const teammates = commits
        .flatMap((it) => it)
        .filter((it) => !currentUserEmails.includes(it.email))
        .filter(
          (it) =>
            !codeService.emailsToIgnore.some((email) =>
              it.email.endsWith(email)
            )
        )
        .map((it) => {
          const sanitizedEmail = sanitize(it.email);

          return {
            ...it,
            id: sanitizedEmail,
            sent: sentEmails.includes(sanitizedEmail),
            placeholder: it.photoURL,
            signedUp: orgMembers.includes(sanitizedEmail),
            photoURL: it.photoURL
          };
        })
        .reduce(
          (acc, teammate) => ({ ...acc, [teammate.email]: teammate }),
          {} as { [key: string]: Teammate }
        );

      // Create the repository for the email template, if there is no repo just use org name
      if (repo) {
        const repository: Repository = {
          displayName: organization.displayName,
          orgOrRepo: codeService.orgText,
          photoURL: organization.photoURL
        };

        setRepository(repository);
      }

      setUser(user);
      setOrganization(organization);
      setTeammates(
        Object.values(teammates).map((it) => ({
          ...it,
          sent: sentEmails.includes(it.id)
        }))
      );
    } catch (err: any) {
      console.error(err);
      if (err instanceof ServiceError) {
        setError(err);
      } else {
        setError(
          new ServiceError(
            defaultError.title,
            err.message || defaultError.message
          )
        );
      }
    }

    setLoading(false);
  };

  const getInviteTokenAndMembers = async (
    token: string,
    service: string,
    host: string,
    username: string | null,
    name: string,
    email: string,
    userId: string,
    owner: Owner,
    scheme: string,
    ide: string,
    machine?: string
  ): Promise<ValidationResponse> => {
    let data: ValidationResponse;
    const validationKey = get(sessionValidationKey);
    if (validationKey) {
      data = JSON.parse(validationKey);
    } else {
      const functions = getFunctions(app);
      const getInviteToken = httpsCallable(functions, 'getInviteToken');
      const response = await getInviteToken({
        token,
        service,
        host,
        username,
        name,
        email,
        userId,
        owner,
        scheme,
        ide,
        machine
      });
      data = response.data as ValidationResponse;

      if (data.error) throw new ServiceError('Failed to sign in', data.error);

      // Save response in session storage in case of user refreshing
      set(sessionValidationKey, JSON.stringify(response.data));
    }

    return data;
  };

  const sendInviteToTeammate = async (email: string) => {
    const invitedUser = teammates.find((it) => it.email === email);
    if (
      !invitedUser ||
      !inviteToken ||
      !user ||
      !organization ||
      !service ||
      !teammates
    ) {
      // TODO: Need to catch this
      throw new ServiceError(inviteError.title, inviteError.message);
    }

    const functions = getFunctions(app);
    const sendInviteEmail = httpsCallable(functions, 'sendInviteEmail');

    const params: InviteTeamMemberParams = {
      token: inviteToken,
      inviter: {
        name: user.displayName,
        photoUrl: user.photoURL
      },
      invited: {
        photoUrl: invitedUser.photoURL,
        name: invitedUser.name,
        email: invitedUser.email
      },
      organization: {
        name: repository ? repository.displayName : organization.displayName,
        photoUrl: organization.photoURL,
        repoOrOrgName: repository ? service.repoText : service.orgText
      },
      teammates: teammates.map((it) => ({
        name: it.name,
        photoUrl: it.photoURL
      }))
    };
    await sendInviteEmail(params);

    // Save emails sent in session storage to avoid being able to send multiple invites if page reloads
    const sentEmailsString = get(sessionEmailsSentKey);
    const sentEmails: string[] = sentEmailsString
      ? JSON.parse(sentEmailsString)
      : [];
    const updatedSentEmails = [...sentEmails, invitedUser.id];
    set(sessionEmailsSentKey, JSON.stringify(updatedSentEmails));
    setTeammates(
      teammates.map((it) => ({
        ...it,
        sent: updatedSentEmails.includes(it.id)
      }))
    );
  };

  return (
    <section className={styles.container}>
      <Helmet>
        <title>{APP_TITLE} - Invite teammates</title>
      </Helmet>
      {error ? (
        <ErrorCard title={error.name} message={error.message} />
      ) : (
        <>
          {service && (
            <InviteTeammatesCard
              loading={loading}
              service={service}
              organization={organization}
              repository={repository}
              teammates={teammates}
              token={token}
              onInviteClicked={sendInviteToTeammate}
            />
          )}
          {!service && (
            <IonSpinner
              color="light"
              name="circles"
              style={{ color: 'white' }}
            />
          )}
        </>
      )}
    </section>
  );
}
